import { useCallback, useEffect, useRef } from "react";

import { serverUpdateAvActiveClassUsersIds } from "api/api-server-actions";
import { ClassSessionCategory } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSessionCategory,
  selectActiveClassSessionId,
  selectAvActiveClassUsersIds,
} from "store/selectors";
import {
  selectCalculatedListForActiveParticipants,
  selectIsParticipantBarLeader,
  selectParticipantBarPipelineCounts,
} from "store/selectors/virtual-class-users-sorting";
import { logInfoWithDebugFlag } from "utils/logger";

export const useDistributedParticipantBarObserver = () => {
  const { canAuthorSection } = useCapabilities();
  const isLeaderOfParticipantBar = useAppSelector(selectIsParticipantBarLeader);
  const participantBarList = useAppSelector(selectAvActiveClassUsersIds);
  const calculatedList = useAppSelector(selectCalculatedListForActiveParticipants);
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const activeClassSessionCategory = useAppSelector(selectActiveClassSessionCategory);
  const isValidClassSession =
    activeClassSessionId && activeClassSessionCategory === ClassSessionCategory.VIRTUAL;

  const participantBarPipelineCounts = useAppSelector(selectParticipantBarPipelineCounts);

  const pipelineDebugKey = useRef<string>("");

  const updateParticipantBarList = useCallback(
    async (newParticipantBarList: string[]) => {
      if (!activeClassSessionId) return;
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[useDistributedParticipantBarObserver] Updating active class users ids",
        newParticipantBarList,
      );
      await serverUpdateAvActiveClassUsersIds(activeClassSessionId, newParticipantBarList);
    },
    [activeClassSessionId],
  );

  // Debug log the participant bar pipeline counts when this is the leader
  useEffect(() => {
    if (!isValidClassSession || !isLeaderOfParticipantBar) return;
    const debugKey = JSON.stringify(participantBarPipelineCounts);
    if (debugKey === pipelineDebugKey.current) return;
    pipelineDebugKey.current = debugKey;
    logInfoWithDebugFlag(
      "debug_virtual_class",
      "[useDistributedParticipantBarObserver] Leader Pipeline counts",
      participantBarPipelineCounts,
    );
  }, [isValidClassSession, participantBarPipelineCounts, isLeaderOfParticipantBar]);

  useEffect(() => {
    // Only section staff can PATCH class_sessions; "leader" can be a student when only students are present
    if (!canAuthorSection || !isLeaderOfParticipantBar || !isValidClassSession) return;
    // If the result of the calculation is the same as the current participant bar list, do nothing.
    if (
      participantBarList.length === calculatedList.length &&
      participantBarList.every((id, index) => id === calculatedList[index])
    )
      return;
    updateParticipantBarList(calculatedList);
  }, [
    canAuthorSection,
    isLeaderOfParticipantBar,
    calculatedList,
    participantBarList,
    isValidClassSession,
    updateParticipantBarList,
  ]);
};
